import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { Box, Grid } from '../../../1-primative';
import { ModelPortfolioData } from '../../../4-module/workflowCompletion/subSteps';
import {
  Card, CardContent, TranslatableTextField, Button,
} from '../../../2-component';
import { AssetClassesTargetAllocations } from '../../../4-module/workflowCompletion/subSteps/addModelPortfoliosIPSAndAssetClasses/components/assetClassesTargetAllocations';
import { AssetClassTargetAllocation } from '../../../4-module/workflowCompletion/subSteps/addModelPortfoliosIPSAndAssetClasses/addModelPortfoliosIPSAndAssetClasses.visual';
import { TranslatedString } from '../../../../interfaces';
import { UPDATE_MODEL_PORTFOLIO } from '../../../4-module/workflowCompletion/subSteps/queries';
import { useGlobalToast } from '../../../../providers/globalToastProvider';
import { formatTargetAllocations } from '../../../4-module/workflowCompletion/subSteps/utils/useSaveModelPortfolio';
import { TriggerResignIpsDialog } from './triggerResignIpsDialog';
import TranslatableRichTextEditor from '../../../3-pattern/translatableRichTextEditor/translatableRichTextEditor';
import { hasModelPortfolioChanges } from '../../../4-module/createModelPortfolioworkflow/createModelPortfolioWorkflow';

export const formatTranslatedField = (value: TranslatedString): TranslatedString | null => ((value.en || value.fr) ? { en: value.en, fr: value.fr } : null);

const ModelPortfolioPolicyAndAssets = ({
  model,
  setModel,
  loading,
}: {
  model: ModelPortfolioData,
  setModel: (v: ModelPortfolioData) => void,
  loading: boolean,
}) => {
  const { t } = useTranslation(['modelPortfolios', 'shared', 'buildModels']);
  const { showToast } = useGlobalToast();
  const [showTriggerResignIpsDialog, setShowTriggerResignIpsDialog] = useState(false);
  const [updateModelPortfolio, { error, loading: updateLoading }] = useMutation(UPDATE_MODEL_PORTFOLIO, {
    refetchQueries: ['fetchModelPortfolio'],
  });
  const [portfolioToUpdate, setPortfolioToUpdate] = useState<ModelPortfolioData>(model);

  const handleUpdateDescription = (value: TranslatedString) => {
    setPortfolioToUpdate((prev) => ({
      ...prev,
      translatedPortfolioDescription: value || { en: '', fr: '' },
    }));
  };

  const handleUpdateObjective = (value: TranslatedString) => {
    setPortfolioToUpdate((prev) => ({
      ...prev,
      translatedInvestmentObjective: value || { en: '', fr: '' },
    }));
  };

  const handleUpdateLiquidity = (value: TranslatedString) => {
    setPortfolioToUpdate((prev) => ({
      ...prev,
      translatedLiquidity: value || { en: '', fr: '' },
    }));
  };

  const handleUpdateDisclaimer = (value: TranslatedString) => {
    setPortfolioToUpdate((prev) => ({
      ...prev,
      translatedPortfolioDisclaimer: value || { en: '', fr: '' },
    }));
  };

  const handleTargetAllocationsChange = (newTargetAllocations: AssetClassTargetAllocation[]) => {
    setPortfolioToUpdate((prev) => ({
      ...prev,
      targetAllocations: newTargetAllocations || [],
    }));
  };

  const handleCancel = () => {
    setPortfolioToUpdate(model);
  };

  const handleSave = async () => {
    if (!portfolioToUpdate?.id) return;

    try {
      const result = await updateModelPortfolio({
        variables: {
          input: {
            modelPortfolioId: portfolioToUpdate.id,
            translatedPortfolioDescription: formatTranslatedField(portfolioToUpdate.translatedPortfolioDescription || { en: '', fr: '' }),
            translatedInvestmentObjective: formatTranslatedField(portfolioToUpdate.translatedInvestmentObjective || { en: '', fr: '' }),
            translatedLiquidity: formatTranslatedField(portfolioToUpdate.translatedLiquidity || { en: '', fr: '' }),
            translatedPortfolioDisclaimer: formatTranslatedField(portfolioToUpdate.translatedPortfolioDisclaimer || { en: '', fr: '' }),
            targetAllocations: formatTargetAllocations(portfolioToUpdate.targetAllocations || []),
          },
        },
      });

      if (result.data?.updateModelPortfolio) {
        setPortfolioToUpdate(portfolioToUpdate);
        setModel(portfolioToUpdate);
        showToast({
          message: t('modelPortfolios:portfolio.success'),
          severity: 'success',
        });
      } else {
        showToast({
          message: error?.message || t('modelPortfolios:portfolio.error'),
          severity: 'error',
        });
      }
    } catch (e) {
      // Error is already handled by mutation
    }
  };

  const hasChanges = useMemo(() => hasModelPortfolioChanges(portfolioToUpdate, model), [model, portfolioToUpdate]);

  return (
    <Box>
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TranslatableTextField
                    testId="portfolio-description"
                    label={t('modelPortfolios:portfolio.policyAndAssets.description')}
                    value={portfolioToUpdate?.translatedPortfolioDescription || { en: '', fr: '' }}
                    onChange={handleUpdateDescription}
                    disabled={loading}
                    fullWidth
                    multiline
                    rows={3}
                    required={true}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TranslatableTextField
                    testId="investment-objective"
                    label={t('modelPortfolios:portfolio.policyAndAssets.investmentObjective')}
                    value={portfolioToUpdate?.translatedInvestmentObjective || { en: '', fr: '' }}
                    onChange={handleUpdateObjective}
                    disabled={loading}
                    fullWidth
                    multiline
                    rows={3}
                    required={true}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TranslatableTextField
                    testId="liquidity"
                    label={t('modelPortfolios:portfolio.policyAndAssets.liquidity')}
                    value={portfolioToUpdate?.translatedLiquidity || { en: '', fr: '' }}
                    onChange={handleUpdateLiquidity}
                    disabled={loading}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 1, minHeight: '200px' }}>
                    <TranslatableRichTextEditor
                      testId="disclaimer-editor-details"
                      value={portfolioToUpdate?.translatedPortfolioDisclaimer || { en: '', fr: '' }}
                      setValue={handleUpdateDisclaimer}
                      id="disclaimer-editor"
                      sx={{ minHeight: '100px' }}
                      label={t('modelPortfolios:portfolio.policyAndAssets.disclaimer')}
                      fullToolbar={false}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <AssetClassesTargetAllocations
                testId="model-portfolio-policy-and-asset-classes-target-allocations"
                targetAllocations={portfolioToUpdate?.targetAllocations as AssetClassTargetAllocation[] || []}
                setTargetAllocations={handleTargetAllocationsChange}
                isFreeForm={true}
                securities={portfolioToUpdate?.children || []}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" padding={2}>
        <Box>
          <Button
            dataTestId="model-portfolio-policy-and-assets-resign-ips-button"
            variant="filled"
            label={t('buildModels:edit.triggerReSign.button')}
            onClick={() => setShowTriggerResignIpsDialog(true)}
            size="sm"
          />
        </Box>

        <Box display="flex" gap={1}>
          <Button
            dataTestId="model-portfolio-policy-and-assets-cancel-button"
            variant="tonal"
            label={t('shared:cancel')}
            onClick={handleCancel}
            size="sm"
            disabled={!hasChanges}
          />
          <Button
            dataTestId="model-portfolio-policy-and-assets-save-button"
            variant="filled"
            label={t('shared:save')}
            onClick={handleSave}
            loading={updateLoading}
            size="sm"
            disabled={!hasChanges}
          />
        </Box>
      </Box>

      <TriggerResignIpsDialog
        open={showTriggerResignIpsDialog}
        onClose={() => setShowTriggerResignIpsDialog(false)}
        modelId={portfolioToUpdate?.id || ''}
      />
    </Box>
  );
};

export default ModelPortfolioPolicyAndAssets;
